import React, { useState, useEffect, useRef } from 'react';
import { noteApi } from '../utils/api';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardBody, Badge, Row, Col, ListGroup, ListGroupItem, Spinner, Button, Alert } from 'reactstrap';

const Statistics = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [message, setMessage] = useState(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const res = await noteApi.get('stats');
            setStats(res.data);
        } catch (err) {
            console.error("Failed to fetch stats", err);
        }
    };

    const handleTagClick = (tag) => {
        navigate(`/advanced-search?tags=${encodeURIComponent(tag)}`);
    };

    const handleYearClick = (year) => {
        const from = `${year}-01-01T00:00`;
        const to = `${year}-12-31T23:59`;
        navigate(`/advanced-search?createdFrom=${encodeURIComponent(from)}&createdTo=${encodeURIComponent(to)}`);
    };

    const handleMonthClick = (monthStr) => {
        const [year, month] = monthStr.split('-').map(Number);
        const lastDay = new Date(year, month, 0).getDate();
        
        const from = `${monthStr}-01T00:00`;
        const to = `${monthStr}-${lastDay}T23:59`;
        navigate(`/advanced-search?createdFrom=${encodeURIComponent(from)}&createdTo=${encodeURIComponent(to)}`);
    };

    const handleExport = async () => {
        setIsExporting(true);
        setMessage(null);
        try {
            const response = await noteApi.get('backup/export', {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'secondbrain_backup.zip';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch.length === 2) filename = filenameMatch[1];
            }
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            setMessage({ type: 'success', text: t('export_success') });
        } catch (err) {
            console.error("Export failed", err);
            setMessage({ type: 'danger', text: t('export_error') });
        } finally {
            setIsExporting(false);
        }
    };

    const handleImportClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsImporting(true);
        setMessage(null);
        const formData = new FormData();
        formData.append('file', file);

        try {
            await noteApi.post('backup/import', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            setMessage({ type: 'success', text: t('import_success') });
            fetchStats(); // Odśwież statystyki po imporcie
        } catch (err) {
            console.error("Import failed", err);
            setMessage({ type: 'danger', text: t('import_error') });
        } finally {
            setIsImporting(false);
            e.target.value = null; // Reset inputa
        }
    };

    if (!stats) return (
        <div className="container mt-5 text-center">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">{t('preparing_data')}</p>
        </div>
    );

    return (
        <div className="container mt-4 pb-5 animate-fade-in">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                <h2 className="mb-0 fw-bold">{t('statistics_title')}</h2>
                <div className="d-flex gap-2">
                    <Button 
                        color="outline-primary" 
                        size="sm" 
                        className="rounded-pill px-3 shadow-sm"
                        onClick={handleExport}
                        disabled={isExporting}
                    >
                        {isExporting ? <Spinner size="sm" /> : '📤 ' + t('export_backup')}
                    </Button>
                    <Button 
                        color="outline-success" 
                        size="sm" 
                        className="rounded-pill px-3 shadow-sm"
                        onClick={handleImportClick}
                        disabled={isImporting}
                    >
                        {isImporting ? <Spinner size="sm" /> : '📥 ' + t('import_backup')}
                    </Button>
                    <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleFileChange} 
                        style={{ display: 'none' }} 
                        accept=".zip"
                    />
                </div>
            </div>

            {message && (
                <Alert color={message.type} toggle={() => setMessage(null)} className="rounded-4 shadow-sm border-0 mb-4">
                    {message.text}
                </Alert>
            )}
            
            <Row className="g-4 mb-5">
                <Col md={4}>
                    <Card className="border-0 shadow-sm bg-primary text-white h-100 hover-card" style={{ borderRadius: '15px', cursor: 'pointer' }} onClick={() => navigate('/advanced-search?showAll=true')}>
                        <CardBody className="text-center p-4">
                            <div className="h1 mb-2">📓</div>
                            <h5 className="text-white-50">{t('total_notes')}</h5>
                            <p className="display-4 fw-bold mb-0">{stats.totalNotes}</p>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm bg-success text-white h-100 hover-card" style={{ borderRadius: '15px', cursor: 'pointer' }} onClick={() => navigate('/advanced-search?hasAttachments=true')}>
                        <CardBody className="text-center p-4">
                            <div className="h1 mb-2">📎</div>
                            <h5 className="text-white-50">{t('with_attachments')}</h5>
                            <p className="display-4 fw-bold mb-0">{stats.notesWithAttachments}</p>
                        </CardBody>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm bg-info text-white h-100 hover-card" style={{ borderRadius: '15px', cursor: 'pointer' }} onClick={() => navigate('/advanced-search?anyTags=true')}>
                        <CardBody className="text-center p-4">
                            <div className="h1 mb-2">🏷️</div>
                            <h5 className="text-white-50">{t('total_tags')}</h5>
                            <p className="display-4 fw-bold mb-0">{stats.totalTags}</p>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <Row className="g-4">
                <Col lg={4}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '15px' }}>
                        <div className="card-header bg-transparent fw-bold py-3 border-0 text-uppercase small text-muted">{t('top_tags')}</div>
                        <ListGroup flush>
                            {Object.entries(stats.topTags || {}).sort((a,b) => b[1] - a[1]).map(([tag, count]) => (
                                <ListGroupItem 
                                    key={tag} 
                                    className="d-flex justify-content-between align-items-center border-0 px-3 py-2 list-item-custom"
                                    action
                                    onClick={() => handleTagClick(tag)}
                                >
                                    <span className="text-primary fw-medium">#{tag}</span>
                                    <Badge color="primary" pill>{count}</Badge>
                                </ListGroupItem>
                            ))}
                        </ListGroup>
                    </Card>
                </Col>
                <Col lg={4}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '15px' }}>
                        <div className="card-header bg-transparent fw-bold py-3 border-0 text-uppercase small text-muted">{t('yearly_activity')}</div>
                        <ListGroup flush>
                            {Object.entries(stats.notesPerYear || {}).sort((a,b) => b[0] - a[0]).map(([year, count]) => (
                                <ListGroupItem 
                                    key={year} 
                                    className="d-flex justify-content-between align-items-center border-0 px-3 py-2 list-item-custom"
                                    action
                                    onClick={() => handleYearClick(year)}
                                >
                                    <span className="fw-medium">{year}</span>
                                    <Badge color="secondary" pill outline="true">{count}</Badge>
                                </ListGroupItem>
                            ))}
                        </ListGroup>
                    </Card>
                </Col>
                <Col lg={4}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '15px' }}>
                        <div className="card-header bg-transparent fw-bold py-3 border-0 text-uppercase small text-muted">{t('monthly_activity')}</div>
                        <ListGroup flush>
                            {Object.entries(stats.notesPerMonth || {}).sort((a,b) => b[0].localeCompare(a[0])).map(([month, count]) => (
                                <ListGroupItem 
                                    key={month} 
                                    className="d-flex justify-content-between align-items-center border-0 px-3 py-2 list-item-custom"
                                    action
                                    onClick={() => handleMonthClick(month)}
                                >
                                    <span className="fw-medium">{month}</span>
                                    <Badge color="light" pill className="text-dark border">{count}</Badge>
                                </ListGroupItem>
                            ))}
                        </ListGroup>
                    </Card>
                </Col>
            </Row>

            <style>
                {`
                .hover-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
                .hover-card:hover { transform: translateY(-5px); box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important; }
                .list-item-custom {
                    background-color: var(--sb-card-bg)!important;
                    color: var(--sb-text)!important;
                    transition: background-color 0.2s ease;
                }
                .list-item-custom:hover {
                    background-color: var(--sb-bg)!important;
                }
                .text-white-50 { color: rgba(255, 255, 255, 0.7)!important; }
                `}
            </style>
        </div>
    );
};

export default Statistics;
